import { FC } from 'react';

import { Controller, useFormContext } from 'react-hook-form-mui';
import { ArrowIcon } from 'assets';
import { Button, Input, PhoneInput } from 'view/components';
import * as S from 'view/modals/OnboardingWizard/styled';
import { LocationAutocomplete } from 'view/pages/profile/components';
import IndustryDropdownAutocomplete from 'view/pages/profile/components/IndustryDropdownAutocomplete';
import JobTitleDropdownAutocomplete from 'view/pages/profile/components/JobTitleDropdownAutocomplete';

interface PersonalInfoStepProps {
  onNext: () => void;
  onBack: () => void;
  isSaving?: boolean;
}

const RecruiterPersonalInfoStep: FC<PersonalInfoStepProps> = ({
  onNext,
  onBack,
  isSaving = false,
}) => {
  const {
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext();

  const firstNameValue = watch('firstName') ?? '';
  const lastNameValue = watch('lastName') ?? '';
  const contactEmailValue = watch('contactEmail') ?? '';
  const currentLocationValue = watch('currentLocation') ?? null;
  const industryValue = watch('industry') ?? null;
  const jobTitleValue = watch('jobTitle') ?? null;

  // Phone is optional, so not included in validation
  const isValid =
    firstNameValue &&
    lastNameValue &&
    contactEmailValue &&
    currentLocationValue &&
    !errors?.firstName &&
    !errors?.lastName &&
    !errors?.contactEmail;

  return (
    <>
      <S.WizardContent>
        <S.StepHeader>
          <S.StepTitle>Personal Information</S.StepTitle>
          <S.StepSubtitle>
            We&apos;ll use this information to personalize your experience and connect you with the
            right candidates
          </S.StepSubtitle>
        </S.StepHeader>
        <S.StepFormContainerCompact>
          <S.InputWithCheckboxWrapper>
            <Input
              label="First Name"
              name="firstName"
              type="text"
              placeholder="Enter your first name"
              marginbottom={0}
            />
          </S.InputWithCheckboxWrapper>
          <S.InputWithCheckboxWrapper>
            <Input
              label="Last Name"
              name="lastName"
              type="text"
              placeholder="Enter your last name"
              marginbottom={0}
            />
          </S.InputWithCheckboxWrapper>
          <S.InputWithCheckboxWrapper>
            <Input
              label="Email Address"
              name="contactEmail"
              type="email"
              placeholder="e.g. youremail@email.com"
              marginbottom={8}
            />
            <S.CheckboxWithText>
              <Controller
                name="showEmail"
                control={control}
                render={({ field }) => {
                  return (
                    <S.CustomCheckbox
                      checked={field.value || false}
                      onChange={(e) => {
                        return field.onChange(e.target.checked);
                      }}
                    />
                  );
                }}
              />
              <p>Use this email for candidate communication</p>
            </S.CheckboxWithText>
          </S.InputWithCheckboxWrapper>
          <S.InputWithCheckboxWrapper>
            <Controller
              name="contactPhone"
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <PhoneInput
                    label="Phone (optional)"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                    marginbottom={8}
                  />
                );
              }}
            />
            <S.CheckboxWithText>
              <Controller
                name="showPhone"
                control={control}
                render={({ field }) => {
                  return (
                    <S.CustomCheckbox
                      checked={field.value || false}
                      onChange={(e) => {
                        return field.onChange(e.target.checked);
                      }}
                    />
                  );
                }}
              />
              <p>Use this phone for candidate communication</p>
            </S.CheckboxWithText>
          </S.InputWithCheckboxWrapper>
          <S.InputWithCheckboxWrapper>
            <LocationAutocomplete
              setValue={setValue}
              currentLocationValue={currentLocationValue}
              label="Location"
            />
          </S.InputWithCheckboxWrapper>
          <S.InputWithCheckboxWrapper>
            <JobTitleDropdownAutocomplete setValue={setValue} jobTitleValue={jobTitleValue} />
          </S.InputWithCheckboxWrapper>
          <IndustryDropdownAutocomplete
            setValue={setValue}
            industryValue={industryValue}
            label="Industry"
          />
        </S.StepFormContainerCompact>
      </S.WizardContent>
      <S.WizardFooter>
        <Button variant="outlined" text="Back" onClick={onBack} disabled={isSaving} />
        <Button
          variant="contained"
          text={isSaving ? 'Saving...' : 'Next'}
          onClick={onNext}
          disabled={!isValid || isSaving}
          endIcon={<ArrowIcon rotate="right" color="#FFFFFF" />}
        />
      </S.WizardFooter>
    </>
  );
};

export default RecruiterPersonalInfoStep;
